import type { Lang } from './format';

/** A message with plural forms; `other` is required. Selected with Intl.PluralRules({count}). */
export interface Plural {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

export type Messages = { readonly [key: string]: string | Plural | Messages };

const PLURAL_KEYS = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);

function isPlural(node: unknown): node is Plural {
  return typeof node === 'object' && node !== null && 'other' in node && Object.keys(node).every((k) => PLURAL_KEYS.has(k));
}

/** Every dotted key of a dictionary, plural groups being leaves. */
export type MessageKey<T> = {
  [K in keyof T & string]: T[K] extends string ? K : T[K] extends Plural ? K : `${K}.${MessageKey<T[K]>}`;
}[keyof T & string];

/** Arabic must provide every key English has (and the same plural-or-not shape). */
export type Translation<T> = { readonly [K in keyof T]: T[K] extends string ? string : T[K] extends Plural ? Plural : Translation<T[K]> };

export type Vars = Record<string, string | number>;

function lookup(messages: Messages, key: string): string | Plural | undefined {
  let node: unknown = messages;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' || isPlural(node) ? node : undefined;
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

/**
 * Builds t(): looks the key up in the language, falls back to English, then to the key itself.
 * A plural group picks its form from `vars.count` with the language's plural rules (and its
 * `zero` form for 0 whenever it has one).
 */
export function createTranslator<T extends Messages>(lang: Lang, dictionaries: { en: T; ar: Translation<T> }) {
  const rules = new Intl.PluralRules(lang);
  const own = (lang === 'ar' ? dictionaries.ar : dictionaries.en) as Messages;
  const fallback = dictionaries.en as Messages;
  return function t(key: MessageKey<T>, vars?: Vars): string {
    const entry = lookup(own, key) ?? lookup(fallback, key);
    if (entry === undefined) return key;
    if (typeof entry === 'string') return interpolate(entry, vars);
    const count = typeof vars?.count === 'number' ? vars.count : Number(vars?.count ?? 0);
    // English has no "zero" category, but "No stops" reads better than "0 stops": a given zero form wins
    const form = count === 0 && entry.zero !== undefined ? 'zero' : (rules.select(count) as keyof Plural);
    return interpolate(entry[form] ?? entry.other, vars);
  };
}
